
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class Camera extends Vue {
  @Prop({ default: null })
  private cameraId!: string | null;

  capturedImageName: string | null = null;
  capturedImage: Blob | null = null;
  capturedImageDataUri: string | null = null;
  isStreaming = false;
  mediaStream: MediaStream | null = null;
  errorMessage = '';

  get constraints() {
    return this.cameraId ?
        { video: { deviceId: this.cameraId } } :
        { video: true };
  }

  get videoElement(): HTMLVideoElement | null {
    return this.$refs.videoElement as HTMLVideoElement ?? null;
  };

  beforeDestroy(): void {
    this.stopCamera();
  }

  async startCamera(): Promise<void> {
    try {
      const constraints = this.constraints;

      // Request webcam access
      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (this.videoElement) {
        this.videoElement.srcObject = this.mediaStream;
        this.isStreaming = true;
        this.errorMessage = '';
      }
    } catch (error) {
      console.error('Error accessing webcam:', error);
      this.errorMessage = this.$t('common.camera.error.pleasePermitCamera').toString();
    }
  }

  stopCamera(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.isStreaming = false;
      this.capturedImageDataUri = '';
      this.errorMessage = '';

      // Clear video element source to release stream
      const oldVideoElement: HTMLVideoElement | null = this.$refs.videoElement as HTMLVideoElement;
      if (oldVideoElement) {
        oldVideoElement.srcObject = null;
      }
    }
  }

  takePhoto(): void {
    if (!this.videoElement || !this.isStreaming) {
      return;
    }

    // Canvas for capturing frame
    const canvas: HTMLCanvasElement | null = document.createElement('canvas');

    try {
      canvas.width = this.videoElement.videoWidth;
      canvas.height = this.videoElement.videoHeight;

      // Draw current video frame to canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(this.videoElement, 0, 0);
        canvas.toBlob(b => {
          this.setFileAndName(b, 'photo.jpg');
        }, 'image/jpeg', 0.9);
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
      this.errorMessage = this.$t('common.camera.error.captureFailed').toString();
    }
  }

  handleFileSelect(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!target.files || !target.files[0]) {
      return;
    }

    this.setFileAndName(target.files[0], target.files[0].name);
  }

  clearImage(): void {
    this.setFileAndName(null, null);
    (this.$refs.fileInput as HTMLInputElement).value = '';
  }

  private setFileAndName(file: Blob | null, fileName: string | null) {
    this.capturedImage = file;
    this.capturedImageName = fileName;
    this.$emit('changed', { file: file, fileName: fileName });
  }

  @Watch('capturedImage', { immediate: true })
  onCapturedImageChanged() {
    if (!this.capturedImage) {
      this.capturedImageDataUri = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => this.capturedImageDataUri = e.target?.result as string;
    reader.readAsDataURL(this.capturedImage);
  }
}
